import React, {useEffect, useRef, useState} from 'react';
import {RouteWithProfileAndProposalId} from '../../routes/types';
import {useHistory, useParams} from 'react-router-dom';
import {clientManagementApiClient} from '../ClientManagementApiClient';
import {
    getSampleTypeValue,
    isNationalSampleSelection,
    ProfileRequestValidation,
    ProfileResponse,
    validateProfileResponse,
    validateProfileResponseWithRequiredSampleType
} from '../models/ProfileResponse';
import {Col, Row, Table} from "xps-react";
import {
    AlertBanner,
    AlertMessage,
    Button,
    Dropdown,
    DropdownItem,
    Name,
    Tab,
    TabBar,
    TabPanel,
    TabsProvider
} from "../../components";
import {calculateTotalTax, EffectiveTaxRates} from "../NewClientProfile/EffectiveTaxRates";
import {EffectiveFees} from "../NewClientProfile/EffectiveFees";
import {EffectiveTaxRateType} from "../models/EffectiveTaxRateType";
import {ReferenceDataType} from "../../models/referenceData/ReferenceDataType";
import {FinancialModelSelection} from "../models/FinancialModelSelection";
import {referenceDataClient} from "../../core/ReferenceDataClient";
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import {PrimaryContactInformation} from './PrimaryContactInformation';
import {PrimaryContact} from '../models/PrimaryContact';
import {msalUtils} from "../../MsalUtils";
import {ServiceTeamMember} from "../models/ServiceTeam";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {Partner, PartnerResource, SearchPartnerResults} from "../models/PartnerResource";
import {
    clearPartnerWorkstationState,
    selectLinkedUlek,
    setLinkedClientName,
    setLinkedUlek
} from "../PartnerWorkstation/partnerWorkstationSlice";
import {selectProfile, setProfile} from "../ClientProfile/activeProfileSlice";
import {partnerApiClient} from "../PartnerApiClient";
import {caseInsensitiveEquals} from "../../utils/stringUtils";
import {PARTNER_DASHBOARD_PAGE_SIZES} from "../PartnerDashboard/partnerDashboardConstants";
import {getEmptyProfileSettingsForm, selectProfileSettingsForm, setProfileSettingsForm} from "./ProfileSettingsSlice";
import {wealthManagementApiClient} from "../WealthManagementApiClient";
import NewClientFinancialModel from "../NewClientProfile/NewClientFinancialModel";
import ErrorModal, {emptyErrorModalData, ErrorModalData} from "../../components/Modal/Error/ErrorModal";
import GenericErrorModal, {
    getGenericCommunicationErrorModalData,
    genericEmptyErrorModalData,
    GenericErrorModalData
} from "../../components/Modal/Error/GenericErrorModal";
import useProfileEditableState from "../../hooks/useProfileEditableState";
import {LinkUnlinkPWClientRequest} from "../../Resources/models/ClientDocuments";
import {selectOldServiceTeam, setOldServiceTeam} from "../../Resources/clientMeetingDocumentSlice";
import {resourcesApiClient} from "../../Resources/ResourcesApiClient";
import {TypeaheadSearchBar} from "../../components/TypeaheadSearchBar";
import {NO_OP} from "../../constants/common";
import classNames from "classnames";
import CustomModal from "../../components/Modal/Custom/CustomModal";
import ModalWrapper from "../../components/Modal/ModalWrapper/ModalWrapper";
import {DropdownOnChangeData} from "../../components/Dropdown/Dropdown";
import {AssetsSummary} from "../../Assets/models/Assets";
import {assetsApiClient} from "../../Assets/AssetsApiClient";
import {setClientAssets} from "../../Assets/clientAssetsSlice";
import {HistoryBlockDiscardModal} from "../../components/HistoryBlockDiscardModal/HistoryBlockModal";
import deepEquals from "fast-deep-equal";
import LoadingIndicator from "../../pages/LoadingIndicator";
import ScrollableContainer from "../../components/ScrollableContainer/ScrollableContainer";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";
import {selectIsNationalSampleAdmin} from "../../Authorization/nationalSampleAdminSlice";

export const enum ProfileSettingsType {
    // TODO NEW_PROFILE
    PROFILE = 'PROFILE',
    PROPOSAL = 'PROPOSAL',
    NEW_PROPOSAL = 'NEW_PROPOSAL',
}

export const enum ClientRelationshipType {
    CLIENT = 'Client',
    PROSPECT = 'Prospect',
}

type ProfileSettingsProps = {
    settingsType?: ProfileSettingsType
}

type ProfileSettingsContentProps = {
    showServiceTeamTab: boolean;
    generalSettings: JSX.Element;
    serviceTeamSettings?: JSX.Element;
}

export const ProfileSettingsContent: React.FC<ProfileSettingsContentProps> = ({
                                                                                  showServiceTeamTab,
                                                                                  generalSettings,
                                                                                  serviceTeamSettings
                                                                              }) => {
    return showServiceTeamTab
        ? <TabsProvider tabIdArray={['details', 'serviceTeam']}>
            <TabBar size="medium">
                <Tab name="General"/>
                <Tab name="Service Team"/>
            </TabBar>
            <TabPanel className={'row-margin'}>
                {generalSettings}
                {serviceTeamSettings}
            </TabPanel>
        </TabsProvider>
        : generalSettings
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({settingsType = ProfileSettingsType.PROFILE}) => {
    const history = useHistory();
    const {profileId, proposalId} = useParams<RouteWithProfileAndProposalId>();
    const msal = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [primaryContact, updatePrimaryContact] = useState({} as PrimaryContact);
    const [referenceData, updateReferenceData] = useState({} as ReferenceDataType);
    const [showErrorBanner, updateShowErrorBanner] = useState(false);
    const [saveHasBeenClicked, updateSaveHasBeenClicked] = useState(false);
    const [profileOwner, updateProfileOwner] = useState('');
    const [accountName, updateAccountName] = useState('');
    const [serviceTeam, updateServiceTeam] = useState([] as ServiceTeamMember[]);
    const serviceTeamRef = useRef<ServiceTeamMember[]>([]);
    const [displayNameInteractions, updateDisplayNameInteractions] = useState(false);
    const [clientProfileValidation, updateClientProfileValidation] = useState({
        displayName: null,
        primaryContact: null
    } as ProfileRequestValidation)
    const clientProfile = useAppSelector(selectProfile);
    const linkedUlek = useAppSelector(selectLinkedUlek)!;
    const profileSettingsForm = useAppSelector(selectProfileSettingsForm);
    const displayName = profileSettingsForm.displayName;
    const dispatch = useAppDispatch();
    const [hasProposal, updateHasProposal] = useState(false);
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
    const [proposalError, setProposalError] = React.useState<ErrorModalData>(emptyErrorModalData);
    const [genericError, setGenericError] = React.useState<GenericErrorModalData>(genericEmptyErrorModalData);
    const {isArchiveReadOnly, isNationalSampleReadOnly} = useProfileEditableState();
    const oldServiceTeam = useAppSelector(selectOldServiceTeam);
    const [accessData, setAccessData] = useState({} as AssetsSummary);
    const [initialProfileSettingsForm, setInitialProfileSettingsForm] = useState(profileSettingsForm);
    const [initialProfileType, setInitialProfileType] = useState(false);
    const [showNavigationModal, setShowNavigationModal] = useState(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const serviceTeamChangedRef = useRef(false);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const isNationalSampleAdmin = useAppSelector(selectIsNationalSampleAdmin);
    const [committedIsNationalSample, setCommittedIsNationalSample] = useState(false);
    const profileIsSample = clientProfile.sample;
    const shouldRequireSampleType = profileIsSample && (releaseToggles?.enableStandardizedClientPersonas ?? false);
    const shouldShowSampleTypeDropdown = shouldRequireSampleType && isNationalSampleAdmin;
    const isSelectedNationalSample = profileIsSample && clientProfile.isNationalSample === true;
    const isNationalSample = profileIsSample && committedIsNationalSample;

    useEffect(() => {
        checkFormIsValid();
    }, [displayName, primaryContact, clientProfile.isNationalSample, shouldRequireSampleType]);

    useEffect(() => {
        if (shouldRequireSampleType && !isNationalSampleAdmin && clientProfile.isNationalSample === undefined) {
            dispatch(setProfile({...clientProfile, isNationalSample: false}));
        }
    }, [clientProfile, dispatch, isNationalSampleAdmin, shouldRequireSampleType]);

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            clientManagementApiClient.getProfile(settingsType === ProfileSettingsType.PROPOSAL ? proposalId : profileId),
            referenceDataClient.getReferenceData(),
            assetsApiClient.getAssetsSummary(profileId),
            clientManagementApiClient.hasProposals(profileId)
        ])
            .then(([profile, referenceData, assetResponse, hasProposals]) => {
                if (clientProfile.resourceCode) {
                    profile.resourceCode = clientProfile.resourceCode;
                }
                dispatch(setProfile(profile));

                const profileSettingsFormBackend = {
                    ...profileSettingsForm,
                    displayName: profile.displayName,
                    effectiveTaxRate: {
                        ...profile.effectiveTaxRate,
                        total: calculateTotalTax(profile.effectiveTaxRate.federal, profile.effectiveTaxRate.stateAndLocal)
                    },
                    allocationType: profile.allocationType,
                    accreditedStatus: profile.accreditedStatus,
                    managementFees: profile.managementFees,
                    alignmentStrategy: profile.alignmentStrategy,
                };
                setInitialProfileType(profile.prospect);
                setInitialProfileSettingsForm(profileSettingsFormBackend);
                dispatch(setProfileSettingsForm(profileSettingsFormBackend));
                updateProfileOwner(profile.owner);
                updatePrimaryContact(profile.primaryContact);
                setCommittedIsNationalSample(profile.sample && profile.isNationalSample === true);

                updateReferenceData(referenceData);

                dispatch(setClientAssets(assetResponse));
                setAccessData(assetResponse);
                updateHasProposal(hasProposals);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('An error occurred:', error.message);
            });
    }, [profileId, proposalId]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        if (isNationalSample) {
            serviceTeamRef.current = [];
            updateServiceTeam([]);
            return;
        }

        if (clientProfile.resourceCode) {
            serviceTeamChangedRef.current = false;
            partnerApiClient.getServiceTeam(clientProfile.resourceCode)
                .then((team: ServiceTeamMember[]) => {
                    if (!serviceTeamChangedRef.current) {
                        serviceTeamRef.current = team;
                        updateServiceTeam(team);
                    }
                });
        } else {
            const currentUser = {
                partnerCode: msalUtils.getLanId(msal),
                partnerName: msalUtils.getAccountName(msal),
                phoneNumber: msalUtils.getAccountPhoneNumber(msal),
                email: msalUtils.getAccountEmail(msal),
                title: msalUtils.getAccountTitle(msal)
            };
            serviceTeamRef.current = [currentUser];
            updateServiceTeam([currentUser]);
        }
    }, [clientProfile.resourceCode, isNationalSample, isLoading]);

    useEffect(() => {
        updateAccountName(msalUtils.getAccountName(msal)!);
    }, []);

    useEffect(() => {
        if (clientProfile?.resourceCode !== '') {
            partnerApiClient.getPartnerResources({
                resourceCode: clientProfile.resourceCode,
                partnerCode: msalUtils.getLanId(msal)
            })
                .then((linkedPartnerResource: PartnerResource[]) => {
                    if (linkedPartnerResource !== undefined) {
                        setLinkedPartnerWorkstationClient(linkedPartnerResource[0]);
                    }
                }).catch(error => {
                    console.error('Could not fetch partner resources', error.message);
                }
            );
        }
    }, [clientProfile?.resourceCode]);

    useEffect(() => {
        if (linkedUlek !== "" && linkedUlek !== null && serviceTeam.length > 1 && oldServiceTeam.resourceCode === undefined) {
            const partners: string[] = [];
            serviceTeam?.forEach(member => {
                partners.push(member.partnerCode);
            });

            dispatch(setOldServiceTeam({
                resourceCode: linkedUlek,
                partners
            }));
        }
    }, [linkedUlek, serviceTeam]);

    const checkFormIsValid = () => {
        const profileValidation = shouldRequireSampleType
            ? validateProfileResponseWithRequiredSampleType({
                ...clientProfile,
                displayName,
                primaryContact
            } as ProfileResponse)
            : validateProfileResponse({
                ...clientProfile,
                displayName,
                primaryContact
            } as ProfileResponse)
        updateClientProfileValidation(profileValidation);
        const isValid = Object.values(profileValidation).every(x => x === null);
        updateShowErrorBanner(!isValid);
        return isValid;
    }

    const handleGenericErrorClose = () => {
        setGenericError((currentError) => ({...currentError, isOpen: false}));
    };

    const handleSaveProfile = async () => {
        if (isProfileTypeChanged()) {
            await clientManagementApiClient.convertProspect(profileId)
        }
        return clientManagementApiClient.putProfile({
            ...clientProfile,
            displayName: displayName,
            primaryContact: primaryContact,
            effectiveTaxRate: profileSettingsForm.effectiveTaxRate,
            managementFees: profileSettingsForm.managementFees,
            accreditedStatus: profileSettingsForm.accreditedStatus,
            allocationType: profileSettingsForm.allocationType,
            alignmentStrategy: profileSettingsForm.alignmentStrategy,
            resourceCode: linkedUlek
        }).catch(error => {
            console.error('Could not save profile', error.message)
        });
    };

    const handleSaveProspect = async () => {
        if (isProfileTypeChanged()) {
            await clientManagementApiClient.convertClient(profileId)
        }
        return clientManagementApiClient.putProspect({
            profile: {
                ...clientProfile,
                displayName: displayName,
                primaryContact: primaryContact,
                effectiveTaxRate: profileSettingsForm.effectiveTaxRate,
                managementFees: profileSettingsForm.managementFees,
                accreditedStatus: profileSettingsForm.accreditedStatus,
                allocationType: profileSettingsForm.allocationType,
                alignmentStrategy: profileSettingsForm.alignmentStrategy,
                lastModifiedByName: accountName,
                resourceCode: linkedUlek
            },
            serviceTeamMembers: serviceTeam,
        }).catch(error => {
            console.error('Could not save prospect', error.message)
        });
    };

    const handleSaveSample = () => {
        return clientManagementApiClient.putSample({
            profile: {
                ...clientProfile,
                displayName: displayName,
                primaryContact: primaryContact,
                effectiveTaxRate: profileSettingsForm.effectiveTaxRate,
                managementFees: profileSettingsForm.managementFees,
                accreditedStatus: profileSettingsForm.accreditedStatus,
                allocationType: profileSettingsForm.allocationType,
                alignmentStrategy: profileSettingsForm.alignmentStrategy,
                lastModifiedByName: accountName,
                resourceCode: isSelectedNationalSample
                    ? ''
                    : linkedUlek || clientProfile.resourceCode
            },
            serviceTeamMembers: isSelectedNationalSample ? [] : serviceTeamRef.current,
        }).catch(error => {
            console.error('Could not save sample', error)
            setGenericError(getGenericCommunicationErrorModalData(error))
        });
    };

    const handleCreateNewProposal = async (): Promise<boolean> => {
        setSaveButtonDisabled(true);
        const response = await wealthManagementApiClient.postProposal(profileId, {
            proposalName: profileSettingsForm.displayName,
            effectiveTaxRate: profileSettingsForm.effectiveTaxRate,
            accreditedStatus: profileSettingsForm.accreditedStatus,
            allocationType: profileSettingsForm.allocationType,
            managementFees: profileSettingsForm.managementFees,
            alignmentStrategy: profileSettingsForm.alignmentStrategy
        })

        if (!response.isSuccessStatusCode) {
            setProposalError({
                isOpen: true,
                header: "Unable to save a new proposal",
                message: "If this error remains after retrying to save, please contact the help desk",
                operationId: response.traceId
            });
            console.error('Could not create proposal');
        }
        return response.isSuccessStatusCode;
    };

    const handleSaveProposal = () => {
        return clientManagementApiClient.putProposal(profileId, proposalId, {
            id: proposalId,
            proposalName: profileSettingsForm.displayName,
            effectiveTaxRate: profileSettingsForm.effectiveTaxRate,
            accreditedStatus: profileSettingsForm.accreditedStatus,
            alignmentStrategy: profileSettingsForm.alignmentStrategy,
            allocationType: profileSettingsForm.allocationType,
            managementFees: profileSettingsForm.managementFees,
        }).catch(error => {
            console.error('Could not save proposal', error.message)
        });
    };

    function handleLinkUnLinkPWClient() {
        const isConvertingMySampleToNationalSample = clientProfile.sample
            && !committedIsNationalSample
            && isSelectedNationalSample;

        if (settingsType === ProfileSettingsType.PROFILE && !isConvertingMySampleToNationalSample) {

            let linkUnlinkPWClientRequest: LinkUnlinkPWClientRequest = {}
            const isResourceCodeExist = (oldServiceTeam.resourceCode !== undefined && linkedUlek !== "" && linkedUlek !== null);
            const isResourceCodeUpdated = (isResourceCodeExist && oldServiceTeam.resourceCode !== linkedUlek);
            const isResourceCodeAdded = (isResourceCodeExist && oldServiceTeam.resourceCode === linkedUlek);
            const isResourceCodeDeleted = (oldServiceTeam.resourceCode !== undefined && (linkedUlek === "" || linkedUlek === null));

            if (isResourceCodeUpdated) {
                const addedPartners: string[] = [];
                serviceTeam?.forEach(member => {
                    addedPartners.push(member.partnerCode);
                });

                linkUnlinkPWClientRequest = {
                    resourceCode: oldServiceTeam.resourceCode!,
                    removedPartners: oldServiceTeam.partners,
                    survivedPartyResourceCode: linkedUlek,
                    addedPartners,
                    deleteFlag: 'Y'
                }
            } else if (isResourceCodeAdded) {
                linkUnlinkPWClientRequest = {
                    resourceCode: oldServiceTeam.resourceCode!,
                    addedPartners: oldServiceTeam.partners,
                    deleteFlag: 'N'
                }
            } else if (isResourceCodeDeleted) {
                linkUnlinkPWClientRequest = {
                    resourceCode: oldServiceTeam.resourceCode!,
                    removedPartners: oldServiceTeam.partners,
                    deleteFlag: 'Y'
                }
            }
            if (linkUnlinkPWClientRequest.resourceCode !== undefined) {
                resourcesApiClient.linkUnlinkPWClient(profileId, linkUnlinkPWClientRequest)
                    .catch(error => {
                        console.error('Could not save proposal', error.message)
                    });
            }

        }

    }

    const handleSaveButton = async () => {
        if (checkFormIsValid()) {
            if (isNationalSampleReadOnly) {
                return;
            }

            let handleSave;
            switch (settingsType) {
                case ProfileSettingsType.NEW_PROPOSAL :
                    handleSave = handleCreateNewProposal;
                    break;
                case ProfileSettingsType.PROFILE :
                    if (clientProfile.prospect)
                        handleSave = handleSaveProspect;
                    else if (clientProfile.sample)
                        handleSave = handleSaveSample;
                    else
                        handleSave = handleSaveProfile;
                    break;
                case ProfileSettingsType.PROPOSAL :
                    handleSave = handleSaveProposal;
                    break;
                default :
                    console.error("Unknown ProfileSettingsType : ", settingsType);
                    return;
            }

            handleSave().then((data) => {
                if (data) {
                    setCommittedIsNationalSample(isSelectedNationalSample);
                    setShowNavigationModal(false);
                    handleLinkUnLinkPWClient();
                    resetFormAndLinkedClient();
                    if (history.location.pathname.includes('ClientProfile')) {
                        setIsLoading(true);
                        if (typeof data !== 'boolean') {
                            dispatch(setProfile({
                                ...(('profile' in data) ? data.profile : data),
                                sample: clientProfile.sample,
                                prospect: clientProfile.prospect,
                                isNationalSample: isSelectedNationalSample,
                            }))
                        }
                        history.go(0)
                    } else {
                        history.push({
                            pathname: `/Profile/${profileId}`
                        })
                    }
                }
            });
        } else {
            updateShowErrorBanner(true);
            updateSaveHasBeenClicked(true);
        }
    }

    const isProfileTypeChanged = () => {
        return !deepEquals(initialProfileType, clientProfile.prospect);
    }

    const isFormChanged = () => {
        const updated = {
            ...profileSettingsForm,
            prospect: clientProfile.prospect,
        }
        const initial = {
            ...initialProfileSettingsForm,
            prospect: initialProfileType,
        }
        return !deepEquals(initial, updated);
    };

    const resetFormAndLinkedClient = () => {
        dispatch(setProfileSettingsForm(getEmptyProfileSettingsForm()));
        dispatch(clearPartnerWorkstationState());
    }

    const setLinkedPartnerWorkstationClient = (partnerResource: PartnerResource) => {
        dispatch(setLinkedUlek(partnerResource.resourceCode));
        dispatch(setLinkedClientName(partnerResource.entityName));
    }

    const generalSettings = <GeneralTab displayName={displayName}
                                        displayNameInteractions={displayNameInteractions}
                                        updateDisplayNameInteractions={updateDisplayNameInteractions}
                                        settingsType={settingsType}
                                        clientProfile={clientProfile}
                                        clientProfileValidation={clientProfileValidation}
                                        saveHasBeenClicked={saveHasBeenClicked}
                                        primaryContact={primaryContact}
                                        referenceData={referenceData}
                                        isProfileWithProposalsReadOnly={settingsType === ProfileSettingsType.PROFILE && hasProposal}
                                        isArchiveReadOnly={isArchiveReadOnly}
                                        accessData={accessData}
                                        shouldRequireSampleType={shouldRequireSampleType}
                                        shouldShowSampleTypeDropdown={shouldShowSampleTypeDropdown}/>;

    const serviceTeamSettings = <ServiceTeamTab serviceTeam={serviceTeam}
                                                savedProfileOwner={clientProfile.owner}
                                                profileOwnerFormState={profileOwner}
                                                updateProfileOwnerFormState={updateProfileOwner}
                                                userLanID={msalUtils.getLanId(msal)}
                                                hasLinkedUlek={!!linkedUlek}
                                                disabled={!!linkedUlek && (settingsType === ProfileSettingsType.PROPOSAL || clientProfile.archived)}
                                                isProspect={clientProfile.prospect}
                                                isSample={clientProfile.sample}
                                                isProposal={settingsType === ProfileSettingsType.PROPOSAL}
                                                onPartnerSelect={(clientProfile.prospect || clientProfile.sample)
                                                    ? (partner: Partner) => {
                                                        serviceTeamChangedRef.current = true;
                                                        const updatedServiceTeam = [...serviceTeamRef.current, partner];
                                                        serviceTeamRef.current = updatedServiceTeam;
                                                        updateServiceTeam(updatedServiceTeam)
                                                    }
                                                    : NO_OP}
                                                onRemoveMember={
                                                    (clientProfile.prospect || clientProfile.sample)
                                                        ? (partnerCode) => {
                                                                serviceTeamChangedRef.current = true;
                                                                const updatedServiceTeam = serviceTeamRef.current.filter((member) => member.partnerCode !== partnerCode);
                                                                serviceTeamRef.current = updatedServiceTeam;
                                                                updateServiceTeam(updatedServiceTeam)
                                                        }
                                                        : NO_OP
                                                }
    />;


    const handleProposalRetry = async () => {
        setProposalError({...proposalError, isOpen: false});
        await handleSaveButton();
    }

    const handleProposalCancel = () => {
        setProposalError({...proposalError, isOpen: false});
        setSaveButtonDisabled(false);
    }
    if (isLoading) {
        return <LoadingIndicator/>
    }
    return (
        <div>
            <HistoryBlockDiscardModal
                when={isFormChanged() && showNavigationModal}
            />
            {!isAuthenticated && msalUtils.handleLogin(msal)}
            {isAuthenticated &&
                <ScrollableContainer id={'profile-settings-scroll-container'}>
                    <div className="page-container">
                        <div className="profile-settings">
                            <ProfileSettingsHeader clientProfile={clientProfile}
                                                   settingsType={settingsType}
                                                   hasErrors={showErrorBanner && saveHasBeenClicked}
                                                   onSave={handleSaveButton}
                                                   onCancel={() => {
                                                       setShowNavigationModal(false);
                                                       resetFormAndLinkedClient();
                                                       history.goBack()
                                                   }}
                                                   saveButtonDisabled={saveButtonDisabled}
                                                   showCancelButton={!history.location.pathname.includes('ClientProfile')}
                            />
                            <ProfileSettingsContent
                                showServiceTeamTab={settingsType !== ProfileSettingsType.NEW_PROPOSAL && !isNationalSample}
                                generalSettings={generalSettings}
                                serviceTeamSettings={serviceTeamSettings}

                            />
                            <ErrorModal
                                errorModalData={proposalError}
                                onClickRetry={handleProposalRetry}
                                onClickCancel={handleProposalCancel}
                            />
                            <GenericErrorModal
                                errorModalData={genericError}
                                onClickButton={handleGenericErrorClose}
                                onRequestClose={handleGenericErrorClose}
                            />
                        </div>
                    </div>
                </ScrollableContainer>
            }
        </div>
    );
}

type ProfileSettingsHeaderProps = {
    clientProfile: ProfileResponse,
    settingsType: ProfileSettingsType,
    onCancel: () => void,
    onSave: () => void,
    hasErrors: boolean,
    saveButtonDisabled: boolean
    showCancelButton: boolean
}

const getProfileSettingsHeaderText = (settingsType: ProfileSettingsType, clientProfile: ProfileResponse) => {
    switch (settingsType) {
        case ProfileSettingsType.NEW_PROPOSAL:
            return 'New Proposal';
        case ProfileSettingsType.PROPOSAL:
            return `Proposal Settings - ${clientProfile?.displayName}`;
        case ProfileSettingsType.PROFILE:
            return `Client Profile Settings - ${clientProfile?.displayName}`;
    }
}

const ProfileSettingsHeader: React.FC<ProfileSettingsHeaderProps> = ({
                                                                         clientProfile,
                                                                         settingsType,
                                                                         onCancel,
                                                                         onSave,
                                                                         hasErrors,
                                                                         saveButtonDisabled,
                                                                         showCancelButton
                                                                     }) => {
    const title = getProfileSettingsHeaderText(settingsType, clientProfile);
    return (
        <div className="row-container">
            <Row>
                <span className="font-xxl title">{title}</span>
                <Col>
                    <div className="textalign-right">
                        {showCancelButton && <Button className="marginright-md"
                                                     icon="none"
                                                     id="cancel_new_client_profile_button"
                                                     includeRef={false}
                                                     kind="secondary"
                                                     onClick={onCancel}
                                                     size="medium"
                                                     tabIndex={0}
                                                     type="button"
                        >
                            CANCEL
                        </Button>}
                        <Button
                            icon="none"
                            id="save_new_client_profile_button"
                            includeRef={false}
                            kind="primary"
                            onClick={onSave}
                            size="medium"
                            tabIndex={0}
                            type="button"
                            disabled={saveButtonDisabled}
                        >
                            SAVE
                        </Button>
                    </div>
                </Col>
                {hasErrors && (
                    <AlertMessage
                        className="display-flex justify-content-right alertBanner"
                        id="alertBanner"
                        fullWidth={false}
                        icon="warning"
                        showAlert={true}
                        showCloseBtn={false}
                        type="error"
                    >
                        <b className="alertBannerStyleName">Required fields missing</b>
                        &nbsp; &nbsp;
                        <b className="font-weight-400">All required fields must be entered to save a client
                            profile.</b>
                    </AlertMessage>
                )}
            </Row>
        </div>
    );
}

type GeneralTabProps = {
    displayName: string,
    displayNameInteractions: boolean,
    updateDisplayNameInteractions: (e: boolean) => void,
    settingsType: ProfileSettingsType,
    clientProfile: ProfileResponse,
    clientProfileValidation: ProfileRequestValidation,
    saveHasBeenClicked: boolean,
    primaryContact: PrimaryContact,
    referenceData: ReferenceDataType,
    isProfileWithProposalsReadOnly: boolean,
    isArchiveReadOnly: boolean,
    accessData: AssetsSummary,
    shouldRequireSampleType: boolean,
    shouldShowSampleTypeDropdown: boolean
}

export const GeneralTab: React.FC<GeneralTabProps> = ({
                                                          displayName,
                                                          displayNameInteractions,
                                                          updateDisplayNameInteractions,
                                                          settingsType,
                                                          clientProfile,
                                                          clientProfileValidation,
                                                          saveHasBeenClicked,
                                                          primaryContact,
                                                          referenceData,
                                                          isProfileWithProposalsReadOnly,
                                                          isArchiveReadOnly,
                                                          accessData,
                                                          shouldRequireSampleType,
                                                          shouldShowSampleTypeDropdown
                                                      }) => {
    const profileSettingsForm = useAppSelector(selectProfileSettingsForm);
    const dispatch = useAppDispatch();
    const profileIsSample = useAppSelector(selectProfile).sample;
    const linkedUlek = useAppSelector(selectLinkedUlek)
    const [openConvertToProspectModal, setOpenConvertToProspectModal] = useState(false);
    const [noProspectConversionModal, setNoProspectConversionModal] = useState(false);
    const [openConvertToClientModal, setOpenConvertToClientModal] = useState(false);
    const hasLinkedUlek = linkedUlek;
    const [defaultProfileTypeValue, setDefaultProfileTypeValue] = useState<string>();
    const [sampleTypeInteractions, setSampleTypeInteractions] = useState(false);

    useEffect(() => {
        setDefaultProfileTypeValue(clientProfile.prospect ? "Prospect" : "Client");
    }, [clientProfile.prospect]);

    const handleDropdownChange = (data: DropdownOnChangeData<string>) => {
        const selectedValue = data.itemText;
        setDefaultProfileTypeValue(selectedValue);
        if (selectedValue === 'Prospect' && !clientProfile.prospect) {
            if (!hasLinkedUlek && !accessData.checkNTLinkedAccounts) {
                setOpenConvertToProspectModal(true)
                setNoProspectConversionModal(false)
            } else {
                setNoProspectConversionModal(true)
                setOpenConvertToProspectModal(false)
            }
        } else if (selectedValue === 'Client' && clientProfile.prospect) {
            setOpenConvertToClientModal(true);
        }
    }

    const handleConvertToProspect = () => {
        setOpenConvertToProspectModal(false)
        setDefaultProfileTypeValue('Prospect');
        dispatch(setProfile({...clientProfile, prospect: true}));
    }

    const handleConvertToClient = () => {
        setOpenConvertToClientModal(false);
        setDefaultProfileTypeValue('Client');
        dispatch(setLinkedUlek(""));
        dispatch(setProfile({...clientProfile, prospect: false}));
    }

    const handleCloseModal = () => {
        setOpenConvertToProspectModal(false)
        setNoProspectConversionModal(false)
        setOpenConvertToClientModal(false)
        setDefaultProfileTypeValue(clientProfile.prospect ? ClientRelationshipType.PROSPECT : ClientRelationshipType.CLIENT);
    }

    return (
        <div className="general-tab">
            <Row>
                <Col>
                    <div className="margintop-lg">
                        <span className="h4">{
                            settingsType === ProfileSettingsType.PROFILE
                                ? 'Profile Details'
                                : 'Proposal Details'
                        }</span>
                        <span
                            className="font-default color-text--error marginleft-md">* Required fields</span>
                    </div>
                    <hr className="section"/>
                    <div className="parent">
                        <Name name={displayName}
                              label={"Display Name"}
                              onChange={(e: any) => {
                                  dispatch(setProfileSettingsForm({
                                      ...profileSettingsForm,
                                      displayName: e.target.value
                                  }));
                              }}
                              disabled={isArchiveReadOnly}
                              required={true}
                              error={clientProfileValidation.displayName}
                              hasInteractions={displayNameInteractions}
                              whenUserHasInteracted={() => {
                                  updateDisplayNameInteractions(true)
                              }}
                              forceShowErrors={saveHasBeenClicked}/>
                    </div>
                    {
                        settingsType === ProfileSettingsType.PROFILE &&
                        profileIsSample &&
                        shouldShowSampleTypeDropdown &&
                        <div className="parent paddingtop-md">
                            <label className="h5 label" htmlFor="sampleTypeInput" id="sampleTypeInput-label">
                                Sample Type <i className="color-text--error">*</i>
                            </label>
                            <div style={{paddingLeft: "200px"}}>
                                <Dropdown
                                    id="sampleTypeInput"
                                    name="sampleType"
                                    aria-label="sampleTypeInput"
                                    aria-labelledby="sampleTypeInput-label"
                                    alignRight={false}
                                    disabled={isArchiveReadOnly}
                                    dropdownKind="select"
                                    panelHeight="auto"
                                    labelWidth="300px"
                                    buttonSize="medium"
                                    data-testid="sampleTypeInputId"
                                    defaultText="Select..."
                                    value={getSampleTypeValue(clientProfile.isNationalSample)}
                                    onBlur={() => setSampleTypeInteractions(true)}
                                    onChange={(data: DropdownOnChangeData<string>) => {
                                        setSampleTypeInteractions(true);
                                        dispatch(setProfile({
                                            ...clientProfile,
                                            isNationalSample: isNationalSampleSelection(data.itemText)
                                        }));
                                    }}
                                >
                                    <DropdownItem itemText={'National Sample'} value={'National Sample'}/>
                                    <DropdownItem itemText={'My Sample'} value={'My Sample'}/>
                                </Dropdown>
                            </div>
                            {((sampleTypeInteractions || saveHasBeenClicked) && clientProfileValidation.sampleType) && (
                                <p className="font-default color-text--error font-weight-400 inlineInputError marginleft-md">
                                    {clientProfileValidation.sampleType}
                                </p>
                            )}
                        </div>
                    }
                    {
                        settingsType === ProfileSettingsType.PROFILE &&
                        !profileIsSample &&
                        <div className="parent paddingtop-md">
                            <label className="h5 label profileTypeLabel"
                                   id="profileTypeInput-label"
                                   data-testid="profileTypeLabel"
                                   style={{paddingTop: "10px"}} aria-required={true}
                            >
                                Profile Type
                            </label>
                            <div style={{paddingLeft: "200px"}}>
                                <Dropdown name="profileType"
                                          className="font-bold"
                                          aria-label="profileTypeInput"
                                          aria-labelledby="profileTypeInput-label"
                                          alignRight={false}
                                          disabled={isArchiveReadOnly}
                                          dropdownKind="select"
                                          panelHeight="auto"
                                          labelWidth="300px"
                                          buttonSize="medium"
                                          data-testid="profileTypeInputId"
                                          defaultText={clientProfile.prospect ? ClientRelationshipType.PROSPECT : ClientRelationshipType.CLIENT}
                                          value={defaultProfileTypeValue}
                                          onChange={handleDropdownChange}
                                >
                                    <DropdownItem itemText={ClientRelationshipType.CLIENT}
                                                  value={ClientRelationshipType.CLIENT}
                                                  data-testid="clientDropdownItem"/>
                                    <DropdownItem itemText={ClientRelationshipType.PROSPECT}
                                                  value={ClientRelationshipType.PROSPECT}
                                                  data-testid="prospectDropdownItem"/>
                                </Dropdown>
                            </div>

                            <CustomModal
                                isOpen={openConvertToClientModal}
                                title={'Edit Profile Type'}
                                content={
                                    <>
                                        <p>Are you sure you want to convert this Prospect Profile to a Client
                                            Profile?
                                            The profile will be moved to the Active Client List on the Partner
                                            Dashboard.</p>
                                        <p>To provide access to Service Team, a PW Client Profile must be linked via
                                            Profile Settings</p>
                                    </>
                                }
                                onClickCancel={handleCloseModal}
                                onClickConfirm={handleConvertToClient}
                                cancelText={'CANCEL'}
                                confirmText={'CONFIRM CLIENT CONVERSION'}
                                confirmButtonDestructive={false}
                            />

                            <CustomModal
                                isOpen={openConvertToProspectModal}
                                title={'Edit Profile Type'}
                                content={'Are you sure you want to convert this Client Profile to a Prospect Profile? ' +
                                    'The profile will be moved to the Prospect Client List on the Partner Dashboard. '}
                                onClickCancel={handleCloseModal}
                                onClickConfirm={handleConvertToProspect}
                                cancelText={'CANCEL'}
                                confirmText={'CONFIRM PROSPECT CONVERSION'}
                                confirmButtonDestructive={false}
                            />

                            <ModalWrapper
                                id="edit-profileType-modal"
                                isOpen={noProspectConversionModal}
                                headerText={"Edit Profile Type"}
                                buttons={[
                                    {
                                        text: 'OKAY',
                                        onClick: handleCloseModal,
                                        primary: true,
                                        destructive: false,
                                    }
                                ]}
                                showCloseButton={true}
                                onRequestClose={handleCloseModal}
                            >
                                <div className="font-md">
                                    <AlertBanner
                                        icon={'warning'}
                                        showAlert={true}
                                        message={"Cannot convert Client Profile to Prospect Profile"}
                                    />
                                    <br/>
                                    A Client Profile with a linked Partner Workstation Client and/or Investment
                                    Program,
                                    Partially Owned Investment Account, Northern Trust Bank Account cannot be
                                    converted
                                    to a Prospect Profile.
                                </div>
                            </ModalWrapper>
                        </div>
                    }
                    {clientProfile?.primaryContact
                        ? (<PrimaryContactInformation
                            primaryContact={primaryContact}
                            showPartnerWorkstationLink={(settingsType === ProfileSettingsType.PROFILE) && !clientProfile.prospect && !clientProfile.sample && clientProfile.activeClient}/>)
                        : undefined}
                </Col>
                <Col className="margintop-lg">
                    {referenceData.taxRates && clientProfile?.effectiveTaxRate ? (
                        <EffectiveTaxRates
                            taxRate={profileSettingsForm.effectiveTaxRate}
                            updateTaxRate={(taxRate: EffectiveTaxRateType) => {
                                dispatch(setProfileSettingsForm({
                                    ...profileSettingsForm,
                                    effectiveTaxRate: taxRate
                                }));
                            }}
                            referenceData={referenceData}
                            isReadOnly={isProfileWithProposalsReadOnly || isArchiveReadOnly}/>
                    ) : undefined}
                    {referenceData.accreditedStatus && clientProfile?.accreditedStatus ? (
                        <NewClientFinancialModel financialModel={{
                            accreditedStatus: profileSettingsForm.accreditedStatus,
                            allocationType: profileSettingsForm.allocationType,
                            alignmentStrategy: profileSettingsForm.alignmentStrategy
                        }}
                                                 updateFinancialModel={(financialModel: FinancialModelSelection) => {
                                                     dispatch(setProfileSettingsForm({
                                                         ...profileSettingsForm,
                                                         ...financialModel
                                                     }));
                                                 }}
                                                 referenceData={referenceData}
                                                 disabled={isProfileWithProposalsReadOnly || isArchiveReadOnly}
                                                 disableAlignmentStrategy={settingsType === ProfileSettingsType.PROPOSAL || settingsType === ProfileSettingsType.NEW_PROPOSAL}/>
                    ) : undefined}

                    {referenceData.managementFees && clientProfile?.managementFees ? (
                        <EffectiveFees managementFees={profileSettingsForm.managementFees}
                                       updateManagementFees={((managementFees: string) => {
                                           dispatch(setProfileSettingsForm({
                                               ...profileSettingsForm,
                                               managementFees: managementFees
                                           }));
                                       })}
                                       referenceData={referenceData}
                                       isReadOnly={isProfileWithProposalsReadOnly || isArchiveReadOnly}/>
                    ) : undefined}
                </Col>
            </Row>
        </div>
    )
        ;
}

type ServiceTeamTabProps = {
    serviceTeam: ServiceTeamMember[],
    savedProfileOwner: string,
    profileOwnerFormState: string,
    updateProfileOwnerFormState: (e: string) => void,
    userLanID: string,
    hasLinkedUlek: boolean,
    disabled: boolean,
    isProspect: boolean,
    isProposal: boolean,
    isSample: boolean,
    onPartnerSelect: (partner: Partner) => void;
    onRemoveMember: (partnerCode: string) => void;
}

export const ServiceTeamTab: React.FC<ServiceTeamTabProps> = ({
                                                                  serviceTeam,
                                                                  savedProfileOwner,
                                                                  profileOwnerFormState,
                                                                  hasLinkedUlek,
                                                                  isProspect,
                                                                  isProposal,
                                                                  isSample,
                                                                  onPartnerSelect,
                                                                  onRemoveMember
                                                              }) => {
    const onPerformSearch = (searchTerm: string, page?: number, signal?: AbortSignal): Promise<SearchPartnerResults> => {
        return partnerApiClient.searchPartners(searchTerm, page).then();
    }

    const searchbarHeaders = <div className="search-results-grid">
        <span className="textalign-left"><b>LAN ID</b></span>
        <span className="textalign-left"><b>Partner Name</b></span>
        <span className="textalign-left"><b>Phone Number</b></span>
        <span className="textalign-left"><b>Email Address</b></span>
        <span className="textalign-left"><b>Partner Title / Role</b></span>
    </div>;

    const displaySearchResults = (result: Partner) => {
        const isServiceTeamMemberAlreadySelected = serviceTeam.some(teamMember => teamMember.partnerCode === result.partnerCode);
        return <div className={classNames("search-results-grid", {
            disabled: isServiceTeamMemberAlreadySelected
        })}>
            <span className="textalign-left">{result.partnerCode}</span>
            <span className="textalign-left">{result.partnerName}</span>
            <span className="textalign-left">{result.phoneNumber}</span>
            <span className="textalign-left">{result.email}</span>
            <span className="textalign-left">{result.title}</span>
        </div>
    };

    const columns: any[] = [
        {
            Header: 'Partner Name',
            accessor: 'partnerName',
            maxWidth: 200
        },
        {
            Header: 'Phone Number',
            accessor: 'phoneNumber',
            maxWidth: 200
        },
        {
            Header: 'Email Address',
            accessor: 'email',
            maxWidth: 200
        },
        {
            Header: 'Partner Title / Role',
            accessor: 'title'
        }
    ];

    if ((isProspect || isSample) && !isProposal) {
        columns.push({
                id: 'delete_service_team_member',
                accessor: 'partnerCode',
                Cell: ({value}: { value: string }) => {
                    return !caseInsensitiveEquals(value, savedProfileOwner)
                        ? <Button
                            className={'delete-service-team-button'}
                            kind="borderless"
                            size="small"
                            tabIndex={0}
                            type="button"
                            destructive={true}
                            icon={"left"}
                            iconName={"delete"}
                            onClick={() => {
                                onRemoveMember(value)
                            }}
                        >
                            Remove
                        </Button>
                        : <div/>
                }
            }
        )
    }

    return (
        <div className="service-team-table">
            {!isProspect && !isSample && <div className={"service-team-subtitle"}>
                <span>Service Team partner access is managed within Partner Workstation</span>
            </div>}
            {(isProspect || isSample) && !isProposal && <div className={"prospect-team-subtitle"}>
                <span>Please search by LAN ID in the field below to add a partner to the Service Team</span>
            </div>}
            {(isProspect || isSample) && !isProposal &&
                <div className={'search-bar-container'} data-testid={'partner-searchbar'}>
                    <TypeaheadSearchBar<Partner>
                        performSearch={onPerformSearch}
                        placeholder={'Type LAN ID...'}
                        displayResult={displaySearchResults}
                        resultHeader={searchbarHeaders}
                        onSelect={(partner: Partner) => {
                            const isServiceTeamMemberAlreadySelected = serviceTeam.some(teamMember => teamMember.partnerCode === partner.partnerCode);
                            if (isServiceTeamMemberAlreadySelected) {
                                return;
                            }
                            onPartnerSelect(partner);
                        }}
                    />
                </div>}
            <Table
                data-testid={'service_team_table'}
                id="service_team_table"
                data={serviceTeam}
                key={profileOwnerFormState}
                showPagination={serviceTeam.length > 10}
                defaultPageSize={15}
                pageSizeOptions={PARTNER_DASHBOARD_PAGE_SIZES}
                columns={columns}
            />
            {!hasLinkedUlek && !isProspect && !isSample &&
                <div className={"no-partner-workstation-text"}>
                    <span>This profile is not connected to Partner Workstation</span>
                </div>
            }
        </div>
    );
}

export default ProfileSettings;

